import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { format, parseISO } from 'date-fns'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../common/SafeIcon'
import { reflectionAPI } from '../lib/supabase'

const { FiBookOpen, FiPlus, FiCalendar, FiEdit2, FiTrash2, FiSearch, FiFilter, FiHeart, FiStar, FiTrendingUp, FiTarget, FiZap } = FiIcons

const Reflections = () => {
  const [reflections, setReflections] = useState([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editingReflection, setEditingReflection] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    mood: 'neutral',
    type: 'daily',
    tags: [],
    date: format(new Date(), 'yyyy-MM-dd')
  })

  const moods = [
    { id: 'excellent', name: 'Excellent', emoji: '🌟', color: 'from-forge-400 to-warning-500' },
    { id: 'good', name: 'Good', emoji: '😊', color: 'from-success-400 to-emerald-500' },
    { id: 'neutral', name: 'Neutral', emoji: '😐', color: 'from-gray-400 to-slate-500' },
    { id: 'challenging', name: 'Challenging', emoji: '😔', color: 'from-blue-400 to-indigo-500' },
    { id: 'difficult', name: 'Difficult', emoji: '😰', color: 'from-danger-400 to-pink-500' }
  ]

  const reflectionTypes = [
    { id: 'daily', name: 'Daily Check-in', icon: FiCalendar, description: 'Daily progress and thoughts' },
    { id: 'weekly', name: 'Weekly Review', icon: FiTrendingUp, description: 'Weekly patterns and insights' },
    { id: 'milestone', name: 'Milestone', icon: FiTarget, description: 'Achievement celebrations' },
    { id: 'setback', name: 'Setback', icon: FiZap, description: 'Learning from challenges' },
    { id: 'gratitude', name: 'Gratitude', icon: FiHeart, description: 'Things you\'re grateful for' }
  ]

  const suggestedTags = [
    'motivation', 'progress', 'challenge', 'breakthrough', 'learning',
    'habit-building', 'mindset', 'goals', 'energy', 'focus',
    'wellness', 'fitness', 'productivity', 'relationships', 'creativity'
  ]

  useEffect(() => {
    loadReflections()
  }, [])

  const loadReflections = async () => {
    try {
      const { data, error } = await reflectionAPI.getReflections()
      if (error) throw error
      setReflections(data || [])
    } catch (error) {
      console.error('Error loading reflections:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.title.trim() || !formData.content.trim()) return

    try {
      let result
      if (editingReflection) {
        result = await reflectionAPI.updateReflection(editingReflection.id, formData)
        setReflections(prev => prev.map(r => r.id === editingReflection.id ? result.data : r))
      } else {
        result = await reflectionAPI.createReflection(formData)
        setReflections(prev => [result.data, ...prev])
      }

      setShowForm(false)
      setEditingReflection(null)
      setFormData({
        title: '',
        content: '',
        mood: 'neutral',
        type: 'daily',
        tags: [],
        date: format(new Date(), 'yyyy-MM-dd')
      })
    } catch (error) {
      console.error('Error saving reflection:', error)
    }
  }

  const handleEdit = (reflection) => {
    setEditingReflection(reflection)
    setFormData({
      title: reflection.title,
      content: reflection.content,
      mood: reflection.mood,
      type: reflection.type,
      tags: reflection.tags || [],
      date: reflection.date
    })
    setShowForm(true)
  }

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this reflection?')) return

    try {
      await reflectionAPI.deleteReflection(id)
      setReflections(prev => prev.filter(r => r.id !== id))
    } catch (error) {
      console.error('Error deleting reflection:', error)
    }
  }

  const addTag = (tag) => {
    if (!formData.tags.includes(tag)) {
      setFormData(prev => ({ ...prev, tags: [...prev.tags, tag] }))
    }
  }

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({ ...prev, tags: prev.tags.filter(tag => tag !== tagToRemove) }))
  }

  const filteredReflections = reflections
    .filter(reflection =>
      reflection.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reflection.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
      reflection.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    )
    .filter(reflection => filterType === 'all' || reflection.type === filterType)
    .sort((a, b) => new Date(b.date) - new Date(a.date))

  const getMoodData = (moodId) => moods.find(m => m.id === moodId)
  const getTypeData = (typeId) => reflectionTypes.find(t => t.id === typeId)

  if (loading) {
    return (
      <div className="min-h-screen bg-dominance-gradient flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="relative mb-8">
            <div className="w-20 h-20 border-4 border-forge-400/30 border-t-forge-400 rounded-full animate-spin"></div>
            <div className="absolute inset-0 w-20 h-20 border-4 border-pink-400/30 border-t-pink-400 rounded-full animate-spin"
                 style={{ animationDelay: '0.5s', animationDuration: '1.5s' }}></div>
          </div>
          <h3 className="text-2xl font-bold text-forge-400 mb-2">Loading Reflections</h3>
          <p className="text-forge-300">Preparing your journey insights...</p>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-dominance-gradient">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <SafeIcon icon={FiBookOpen} className="text-2xl text-forge-400" />
            <h1 className="text-4xl md:text-5xl font-bold bg-forge-gradient bg-clip-text text-transparent">
              Mind Forge
            </h1>
          </div>
          <p className="text-xl text-forge-300 max-w-2xl mx-auto mb-8">
            Document your conquest, celebrate victories, and learn from battles
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-forge-gradient text-shadow-950 px-8 py-4 rounded-2xl font-semibold hover:shadow-power transition-all duration-300 hover:scale-105 group"
          >
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiPlus} className="group-hover:rotate-90 transition-transform duration-300" />
              <span>New Reflection</span>
            </div>
          </button>
        </motion.div>

        {/* Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl shadow-power border border-forge-400/30 p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-forge-400" />
              <input
                type="text"
                placeholder="Search reflections..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 bg-shadow-800/50 border border-forge-400/30 rounded-2xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 backdrop-blur-sm text-forge-200 placeholder-forge-300/50"
              />
            </div>
            <div className="flex items-center space-x-2">
              <SafeIcon icon={FiFilter} className="text-forge-400" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-3 bg-shadow-800/50 border border-forge-400/30 rounded-2xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 backdrop-blur-sm text-forge-200"
              >
                <option value="all">All Types</option>
                {reflectionTypes.map(type => (
                  <option key={type.id} value={type.id}>{type.name}</option>
                ))}
              </select>
            </div>
          </div>
        </motion.div>

        {/* Reflections Grid */}
        {filteredReflections.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-20"
          >
            <div className="max-w-md mx-auto">
              <div className="w-24 h-24 bg-forge-gradient rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-power">
                <SafeIcon icon={FiBookOpen} className="text-shadow-950 text-3xl" />
              </div>
              <h3 className="text-2xl font-bold text-forge-400 mb-4">Start Your Reflection Journey</h3>
              <p className="text-forge-300 mb-8 text-lg">
                Begin documenting your thoughts, progress, and insights to accelerate your conquest.
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="bg-forge-gradient text-shadow-950 px-8 py-4 rounded-2xl font-semibold hover:shadow-power transition-all duration-300 hover:scale-105"
              >
                Write Your First Reflection
              </button>
            </div>
          </motion.div>
        ) : (
          <div className="grid gap-6">
            <AnimatePresence>
              {filteredReflections.map((reflection, index) => {
                const moodData = getMoodData(reflection.mood)
                const typeData = getTypeData(reflection.type)

                return (
                  <motion.div
                    key={reflection.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.1 }}
                    className="bg-gradient-to-br from-shadow-900/90 to-shadow-800/90 backdrop-blur-sm rounded-3xl shadow-power border border-forge-400/30 p-8 hover:shadow-dominance transition-all duration-500 group"
                  >
                    <div className="flex items-start justify-between mb-6">
                      <div className="flex items-center space-x-4">
                        <div className={`w-12 h-12 bg-gradient-to-r ${moodData.color} rounded-2xl flex items-center justify-center text-white text-xl shadow-lg`}>
                          {moodData.emoji}
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-forge-400">{reflection.title}</h3>
                          <div className="flex items-center space-x-3 mt-1">
                            <span className="text-sm text-forge-300">{format(parseISO(reflection.date), 'PPP')}</span>
                            <span className="px-3 py-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 text-purple-400 rounded-full text-xs font-medium border border-purple-400/30">
                              {typeData.name}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleEdit(reflection)}
                          className="p-2 text-forge-400 hover:text-forge-200 hover:bg-forge-400/20 rounded-xl transition-all duration-300"
                        >
                          <SafeIcon icon={FiEdit2} />
                        </motion.button>
                        <motion.button
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          onClick={() => handleDelete(reflection.id)}
                          className="p-2 text-forge-400 hover:text-danger-400 hover:bg-danger-400/20 rounded-xl transition-all duration-300"
                        >
                          <SafeIcon icon={FiTrash2} />
                        </motion.button>
                      </div>
                    </div>

                    <div className="prose prose-gray max-w-none mb-6">
                      <p className="text-forge-200 leading-relaxed">{reflection.content}</p>
                    </div>

                    {reflection.tags && reflection.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {reflection.tags.map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="px-3 py-1 bg-gradient-to-r from-shadow-700 to-shadow-600 text-forge-300 rounded-full text-sm hover:bg-gradient-to-r hover:from-forge-400/20 hover:to-forge-500/20 transition-all duration-300 border border-forge-400/20"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )
              })}
            </AnimatePresence>
          </div>
        )}

        {/* Reflection Form Modal */}
        <AnimatePresence>
          {showForm && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                className="bg-gradient-to-br from-shadow-900/95 to-shadow-800/95 backdrop-blur-sm rounded-3xl shadow-dominance max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-forge-400/30"
              >
                <div className="bg-forge-gradient p-6 text-shadow-950 rounded-t-3xl">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold">
                      {editingReflection ? 'Edit Reflection' : 'New Reflection'}
                    </h2>
                    <button
                      onClick={() => {
                        setShowForm(false)
                        setEditingReflection(null)
                        setFormData({
                          title: '',
                          content: '',
                          mood: 'neutral',
                          type: 'daily',
                          tags: [],
                          date: format(new Date(), 'yyyy-MM-dd')
                        })
                      }}
                      className="p-2 hover:bg-shadow-900/20 rounded-xl transition-colors"
                    >
                      <SafeIcon icon={FiTrash2} className="text-xl" />
                    </button>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        Title *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                        className="w-full px-4 py-3 border border-forge-400/30 rounded-2xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 backdrop-blur-sm text-forge-200 placeholder-forge-300/50"
                        placeholder="What's on your mind?"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-forge-300 mb-2">
                        Date
                      </label>
                      <input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
                        className="w-full px-4 py-3 border border-forge-400/30 rounded-2xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 backdrop-blur-sm text-forge-200"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-forge-300 mb-2">
                      Reflection Type
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {reflectionTypes.map(type => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, type: type.id }))}
                          className={`p-3 rounded-2xl text-left transition-all duration-300 ${
                            formData.type === type.id
                              ? 'bg-forge-gradient text-shadow-950 shadow-forge'
                              : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/20 text-forge-200'
                          }`}
                        >
                          <SafeIcon icon={type.icon} className="mb-2" />
                          <div className="text-sm font-medium">{type.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-forge-300 mb-2">
                      How are you feeling?
                    </label>
                    <div className="grid grid-cols-5 gap-3">
                      {moods.map(mood => (
                        <button
                          key={mood.id}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, mood: mood.id }))}
                          className={`p-3 rounded-2xl text-center transition-all duration-300 ${
                            formData.mood === mood.id
                              ? `bg-gradient-to-r ${mood.color} text-white shadow-lg`
                              : 'bg-shadow-800/50 hover:bg-shadow-700/50 border border-forge-400/20 text-forge-200'
                          }`}
                        >
                          <div className="text-2xl mb-1">{mood.emoji}</div>
                          <div className="text-xs font-medium">{mood.name}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-forge-300 mb-2">
                      Your Reflection *
                    </label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                      rows="6"
                      className="w-full px-4 py-3 border border-forge-400/30 rounded-2xl focus:ring-2 focus:ring-forge-400 focus:border-forge-400 bg-shadow-800/50 backdrop-blur-sm resize-none text-forge-200 placeholder-forge-300/50"
                      placeholder="Share your thoughts, insights, challenges, or celebrations..."
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-forge-300 mb-2">
                      Tags
                    </label>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-gradient-to-r from-forge-400/20 to-forge-500/20 text-forge-300 rounded-full text-sm flex items-center space-x-1 border border-forge-400/30"
                        >
                          <span>#{tag}</span>
                          <button
                            type="button"
                            onClick={() => removeTag(tag)}
                            className="text-forge-400 hover:text-forge-200"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {suggestedTags.filter(tag => !formData.tags.includes(tag)).map(tag => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => addTag(tag)}
                          className="px-3 py-1 bg-shadow-800/50 text-forge-300 rounded-full text-sm hover:bg-forge-400/20 transition-colors border border-forge-400/20"
                        >
                          +{tag}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end space-x-3 pt-6 border-t border-forge-400/20">
                    <button
                      type="button"
                      onClick={() => {
                        setShowForm(false)
                        setEditingReflection(null)
                      }}
                      className="px-6 py-3 border border-forge-400/30 text-forge-300 rounded-2xl hover:bg-shadow-700/50 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-3 bg-forge-gradient text-shadow-950 rounded-2xl hover:shadow-power transition-all duration-300"
                    >
                      {editingReflection ? 'Update' : 'Save'} Reflection
                    </button>
                  </div>
                </form>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </div>
  )
}

export default Reflections